import { useEffect, useState } from 'react';
import { useApi } from '../services/api';
import { ActivityEvent, ActivityEventType } from '@bst-stellar/shared';
import { formatDate } from '../utils/dateFormatter';

interface ActivityFeedProps {
    departmentId?: string;
    gameId?: string;
    limit?: number;
}

export default function ActivityFeed({
    departmentId,
    gameId,
    limit = 20,
}: ActivityFeedProps) {
    const api = useApi();
    const [events, setEvents] = useState<ActivityEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    useEffect(() => {
        loadEvents();
    }, [departmentId, gameId, page]);

    async function loadEvents() {
        try {
            setLoading(true);
            const result = await api.getActivity({
                departmentId,
                gameId,
                page,
                pageSize: limit,
            });

            if (page === 0) {
                setEvents(result.events);
            } else {
                setEvents([...events, ...result.events]);
            }

            setHasMore(result.events.length === limit);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }

    function getEventIcon(eventType: ActivityEventType) {
        switch (eventType) {
            case ActivityEventType.CLAIM_CREATED:
                return '📝';
            case ActivityEventType.CLAIM_APPROVED:
                return '✅';
            case ActivityEventType.CLAIM_REJECTED:
                return '❌';
            case ActivityEventType.BINGO_LINE:
                return '🎉';
            case ActivityEventType.GAME_STARTED:
                return '🎮';
            case ActivityEventType.GAME_ENDED:
                return '🏁';
            case ActivityEventType.REVEAL_STARTED:
                return '👀';
            default:
                return '📌';
        }
    }

    function getEventMessage(event: ActivityEvent) {
        const { eventType, eventData } = event;

        switch (eventType) {
            case ActivityEventType.CLAIM_CREATED:
                return `${eventData.userName} submitted a claim for "${eventData.itemTitle}"`;
            case ActivityEventType.CLAIM_APPROVED:
                return `${eventData.userName} completed "${eventData.itemTitle}"`;
            case ActivityEventType.CLAIM_REJECTED:
                if (eventData.isAdmin) {
                    return `You rejected ${eventData.userName}'s claim for "${eventData.itemTitle}"${eventData.rejectionReason ? `: ${eventData.rejectionReason}` : ''}`;
                }
                return `Your claim for "${eventData.itemTitle}" was rejected${eventData.rejectionReason ? `: ${eventData.rejectionReason}` : ''}${eventData.adminName ? ` by ${eventData.adminName}` : ''}`;
            case ActivityEventType.BINGO_LINE:
                return `🎉 ${eventData.userName} completed a bingo line!`;
            case ActivityEventType.GAME_STARTED:
                return `Game "${eventData.gameName}" has started`;
            case ActivityEventType.GAME_ENDED:
                return `Game "${eventData.gameName}" has ended`;
            case ActivityEventType.REVEAL_STARTED:
                return `Reveal started for "${eventData.gameName}"`;
            default:
                return 'Activity event';
        }
    }

    function formatTime(date: Date) {
        const now = new Date();
        const diff = now.getTime() - new Date(date).getTime();
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (minutes < 1) return 'Just now';
        if (minutes < 60) return `${minutes}m ago`;
        if (hours < 24) return `${hours}h ago`;
        if (days < 7) return `${days}d ago`;
        return formatDate(date);
    }

    if (error) {
        return (
            <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded">
                {error}
            </div>
        );
    }

    return (
        <div className="bg-surface rounded-lg shadow border border-border">
            <div className="p-4 border-b border-border">
                <h2 className="text-lg font-semibold text-text-primary">Recent Activity</h2>
            </div>

            <div className="divide-y divide-border max-h-96 overflow-y-auto">
                {loading && page === 0 ? (
                    <div className="p-8 text-center text-text-secondary">Loading...</div>
                ) : events.length === 0 ? (
                    <div className="p-8 text-center text-text-secondary">No activity yet</div>
                ) : (
                    <>
                        {events.map((event) => (
                            <div key={event.id} className="p-4 hover:bg-surface-hover transition">
                                <div className="flex items-start gap-3">
                                    <div className="text-2xl">{getEventIcon(event.eventType)}</div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-text-primary">
                                            {getEventMessage(event)}
                                        </p>
                                        <p className="text-xs text-text-secondary mt-1">
                                            {formatTime(event.createdAt)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}

                        {hasMore && (
                            <div className="p-4 text-center">
                                <button
                                    onClick={() => setPage(page + 1)}
                                    disabled={loading}
                                    className="text-blue-600 hover:text-blue-800"
                                >
                                    {loading ? 'Loading...' : 'Load more'}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
